import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import Loading from 'components/Loading'
import useFetch from 'hooks/useFetch'
import { Fields as Merchant } from 'models/Merchant'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { errorHandler } from 'utilities/errorHandler'
import LookerError from './components/LookerError'
type PropsT = {
  path: string
  merchant: Merchant
  height?: string
  urlParams?: Record<string, string>
  /**
   * Accessible name for the embed. Defaults to `path`, which is what every
   * caller got before this existed — and which a screen reader announces as
   * "dashboards/478", identifying nothing. Pass the report's own title where
   * one is available.
   */
  title?: string
}

const LookerIframe: React.FC<PropsT> = props => {
  const { path, merchant, height: initialHeight, urlParams, title } = props
  const [currentHeight, setCurrentHeight] = useState<string | undefined>(
    initialHeight || undefined
  )
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const elementID = `looker-${path}`

  useEffect(() => {
    const handleHeightChange = (event: MessageEvent) => {
      // Every mounted LookerIframe listens on the same window, so without this
      // guard a height message from ANY embed resizes ALL of them — the last
      // one to report wins. That is not hypothetical: report pages carry
      // several embeds at once (Engagement's two panes are tuned to 2170 and
      // 2430), and TabPane deliberately does not unmount a pane on exit, so
      // every tab a user opens leaves another broadcaster on this window.
      //
      // `iframeRef` was already attached here but never read; this is what it
      // is for.
      if (event.source !== iframeRef.current?.contentWindow) return

      const data =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      // Check if this is a Looker page properties message
      if (data?.type === 'page:properties:changed') {
        if (data.height) {
          setCurrentHeight(`${data.height}px`)
        }
      }
    }

    window.addEventListener('message', handleHeightChange)

    return () => {
      window.removeEventListener('message', handleHeightChange)
    }
  }, [])

  // Constructing URL parameters
  let targetPath = path
  if (urlParams) {
    const params = new URLSearchParams(urlParams)
    targetPath += `?${params.toString()}`
  }

  const config = useMemo(
    () => ({
      method: 'GET',
      url: `${merchant.id}/looker/embed_url`,
      params: {
        target_path: targetPath,
      },
    }),
    [merchant, targetPath]
  )
  const { isLoading, response, errors } = useFetch(config)
  const [css] = useStyletron()

  // A 4xx from /looker/embed_url renders <LookerError /> instead of throwing,
  // so without an explicit capture the broken-iframe state is invisible to
  // error monitoring. Report it with enough context to identify which
  // dashboard/merchant combination is misconfigured (e.g. a path missing from
  // the looker-embed-paths allowlist).
  useEffect(() => {
    if (errors) {
      errorHandler.capture('Looker embed_url request failed', {
        path,
        merchantId: merchant.id,
        errors,
      })
    }
  }, [errors, path, merchant.id])

  if (errors) return <LookerError />

  const url = response?.data.url
  const heightClass = path.includes('dashboard') ? 'h-100' : 'h-50'

  return (
    <div>
      <Switch
        condition={!isLoading}
        fallback={
          <div
            className={`w-100 border-0 ${!currentHeight && heightClass} ${
              currentHeight && css({ height: currentHeight })
            }`}
          >
            <Loading />
          </div>
        }
      >
        <iframe
          data-testid="LookerIframe"
          ref={iframeRef}
          id={elementID}
          src={url}
          title={title ?? path}
          // The catalog id, kept as its own attribute so tests can pin which
          // dashboard a pane renders without asserting on `title` — that is
          // the accessible name now, and a screen reader should hear the
          // report, not "dashboards/478".
          data-path={path}
          className={`w-100 border-0 ${!currentHeight && heightClass} ${
            currentHeight && css({ height: currentHeight })
          }`}
        />
      </Switch>
    </div>
  )
}

export default LookerIframe
